import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { format, endOfWeek, startOfWeek } from "date-fns/fp";
import { useId } from "@reach/auto-id";
import "lodash/fp";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
import { AnimatedPopover } from "../animated-popover/animated-popover.es.js";
import { DatePicker } from "../date-picker/date-picker.es.js";
import { Input } from "../input/input.es.js";
import { MonthPicker } from "../month-picker/month-picker.es.js";
import { WeekPicker } from "../week-picker/week-picker.es.js";
import { formatDateRange } from "../../utils/format-date-range.es.js";
const propTypes = {
  defaultExpanded: PropTypes.bool,
  label: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  minDate: PropTypes.instanceOf(Date),
  mode: PropTypes.oneOf(["date", "month", "week"]),
  onCancel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  textAlign: PropTypes.oneOf(["center", "left", "right"]),
  tone: PropTypes.oneOf(["critical", "neutral", "positive"]),
  value: PropTypes.instanceOf(Date),
  width: PropTypes.number
};
const defaultProps = {
  defaultExpanded: false,
  label: void 0,
  maxDate: void 0,
  message: void 0,
  minDate: void 0,
  mode: "date",
  onCancel: void 0,
  textAlign: void 0,
  tone: void 0,
  value: void 0,
  width: 96
};
const DateInput = ({
  defaultExpanded,
  label,
  maxDate,
  message,
  minDate,
  mode,
  onCancel,
  onChange,
  textAlign,
  tone,
  value,
  width,
  ...restProps
}) => {
  const labelId = `date-input-label__${useId()}`;
  const inputRef = useRef();
  const [isExpanded, setExpanded] = useState(defaultExpanded);
  const valueStr = value ? {
    date: format("d MMM yyyy", value),
    month: format("MMM yyyy", value),
    week: formatDateRange(startOfWeek(value), endOfWeek(value))
  }[mode] : "";
  const handleToggle = useCallback(() => {
    setExpanded((state) => !state);
  }, []);
  const handleClose = useCallback(() => {
    setExpanded(false);
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);
  const handleSelectAndClose = useCallback(
    (date) => {
      onChange(date);
      setExpanded(false);
    },
    [onChange]
  );
  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter") {
      event.stopPropagation();
      setExpanded((state) => !state);
    }
  }, []);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        ref: inputRef,
        expanded: isExpanded,
        label,
        labelId,
        maxWidth: width,
        message,
        onClick: handleToggle,
        onKeyDown: handleKeyDown,
        readOnly: true,
        role: "button",
        textAlign,
        tone,
        value: valueStr,
        ...restProps
      }
    ),
    /* @__PURE__ */ jsxs(
      AnimatedPopover,
      {
        justify: "start",
        offset: 4,
        open: isExpanded,
        placement: "bottom",
        stopPropagation: true,
        targetRef: inputRef,
        children: [
          mode === "date" && /* @__PURE__ */ jsx(
            DatePicker,
            {
              "aria-labelledby": label ? labelId : void 0,
              inputRef,
              maxDate,
              minDate,
              onCancel: handleClose,
              onSelect: onChange,
              onSelectAndClose: handleSelectAndClose,
              value
            }
          ),
          mode === "month" && /* @__PURE__ */ jsx(
            MonthPicker,
            {
              "aria-labelledby": label ? labelId : void 0,
              inputRef,
              maxDate,
              minDate,
              onCancel: handleClose,
              onSelectAndClose: handleSelectAndClose,
              value
            }
          ),
          mode === "week" && /* @__PURE__ */ jsx(
            WeekPicker,
            {
              "aria-labelledby": label ? labelId : void 0,
              inputRef,
              maxDate,
              minDate,
              onCancel: handleClose,
              onSelect: onChange,
              onSelectAndClose: handleSelectAndClose,
              value
            }
          )
        ]
      }
    )
  ] });
};
DateInput.propTypes = propTypes;
DateInput.defaultProps = defaultProps;
export {
  DateInput
};
